import { useCallback } from "react";
import { Helmet } from "react-helmet";
import bm from "../100-board-members/BoardMembers100.module.scss";
import Styles from "./RajeshBhat.module.scss";
import { FaLinkedin } from "react-icons/fa";
import { urls } from "../../utils/urls";

export default function RajeshBhat() {
  const scrollToStory = useCallback(() => {
    document.getElementById("rajesh-intro")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className={bm["page-wrapper"]}>
      <Helmet>
        <title>Rajesh Bhat | Iron Lady</title>
        <meta
          name="description"
          content="Meet Rajesh Bhat — serial social entrepreneur; founder of Head Held High, 1Bridge, Iron Lady, and Winner Bench. Vision, grit, and recognition including CNN Real Heroes of India."
        />
        <link rel="canonical" href="https://iamironlady.com/aboutUs/rajesh-bhat" />
      </Helmet>

      <section className={bm.hero} aria-labelledby="rajesh-hero-heading">
        <div className={bm["hero-overlay"]} aria-hidden />
        <div className={bm["hero-inner"]}>
          <div className={bm["hero-content"]}>
            <div className={bm["hero-eyebrow"]}>About</div>
            <div className={bm["hero-brand"]}>Iron Lady</div>
            <h1 id="rajesh-hero-heading" className={`${bm["hero-tagline"]} ${Styles.rbHeroHeadline}`}>
              Rajesh <span>Bhat.</span>
            </h1>
            <p className={bm["hero-sub"]}>
              Villager turned serial social entrepreneur — Head Held High, 1Bridge, Iron Lady,
              Winner Bench. Real Hero · TOP CEO · spotlight alongside Amitabh Bachchan.
            </p>
            <div className={`${bm["hero-cta"]} ${bm["hero-cta-group"]}`}>
              <button type="button" className={bm["btn-primary"]} onClick={scrollToStory}>
                <span>Read his story</span>
              </button>
              <a
                href="https://www.linkedin.com/in/meetrajeshbhat"
                target="_blank"
                rel="noopener noreferrer"
                className={bm["btn-outline"]}
                aria-label="Rajesh Bhat on LinkedIn"
              >
                <FaLinkedin aria-hidden />
                <span>LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section
        id="rajesh-intro"
        className={`${bm["video-section"]} ${Styles.band}`}
        aria-labelledby="rajesh-intro-heading"
      >
        <div className={Styles.pageInner}>
          <span className={bm.eyebrow}>Founder profile</span>
          <h2 id="rajesh-intro-heading" className={Styles.visuallyHidden}>
            Introduction
          </h2>
          <div className={Styles.leadCard}>
            <p>
              A villager turned serial social entrepreneur. Awarded Real Hero / TOP CEO / Featured
              with Amitabh Bachchan.
            </p>
          </div>

          <div className={Styles.splitCard}>
            <div className={Styles.imageCol}>
              <img src={urls.rajesh} alt="Rajesh Bhat portrait" className={Styles.portrait} />
            </div>
            <div className={Styles.textCol}>
              <p>
                Rajesh Bhat is a serial Social Entrepreneur and founder of initiatives like Head Held
                High, 1Bridge, and Iron Lady. He made headlines by{" "}
                <strong>wearing a saree on a TEDx stage</strong>, becoming the first man to ever do so.
              </p>
              <p>
                It wasn&apos;t just an outfit choice—it was a statement. A stand against rigid societal
                norms. A call for inclusivity and equality.
              </p>
              <p>
                Rajesh started Head Held High in 2007, at the age of 25. He built a{" "}
                <strong>revolutionary model</strong> that transforms completely illiterate rural youth –
                to speak confidently in English and even pass 10th standard, in 6 months.
              </p>
              <p>
                Head Held High has been hailed on all national platforms as a one-of-its-kind platform
                and is now supporting lakhs of youth in 22 states across India!
              </p>
              <p>
                In 2017, Rajesh, along with a few other global leaders, started Iron Lady. Iron Lady has
                now become the No. 1 Leadership Platform for Women in India, supporting thousands of women
                to reach the TOP yearly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={`${bm.authority} ${Styles.bandDark}`} aria-labelledby="early-life-heading">
        <div className={Styles.pageInner}>
          <h2 id="early-life-heading" className={Styles.sectionTitle}>
            Early life
          </h2>
          <div className={Styles.glassCard}>
            <p>
              Rajesh&apos;s own story began in a tiny village in Karnataka.{" "}
              <strong>He was the first in his community to become an engineer</strong>—a feat that
              wasn&apos;t easy but proved that big dreams can come from the smallest places. That journey
              shaped his life&apos;s work to make sure others, no matter where they start, have the chance
              to achieve what once felt impossible.
            </p>
            <p>
              Today, Rajesh continues to inspire, lead, and challenge the world to think differently.
              He&apos;s not just a man in a saree; he&apos;s a man with a mission.
            </p>
          </div>
        </div>
      </section>

      <section className={`${bm["video-section"]} ${Styles.band}`} aria-labelledby="journey-heading">
        <div className={Styles.pageInner}>
          <h2 id="journey-heading" className={Styles.sectionTitle}>
            Rajesh Bhat&apos;s professional journey
          </h2>
          <div className={Styles.glassCard}>
            <p>
              Rajesh Bhat&apos;s professional journey is a story of vision, grit, and relentless impact.
              Over the last 17 years, he has worn many hats—entrepreneur, changemaker, mentor—but at the
              heart of it all, he&apos;s been a builder of possibilities.
            </p>
          </div>

          <div className={Styles.roleSection}>
            <div className={Styles.logoContainer}>
              <img src={urls.headHeldHighLogo} alt="" className={Styles.roleLogo} />
            </div>
            <div>
              <h3 className={Styles.subSectionTitle}>Head Held High Foundation, Co-founder and Trustee</h3>
              <p>
                The transformative initiative established in 2007. Inspired by Rabindranath Tagore&apos;s
                iconic poem{" "}
                <strong>
                  <em>Where the mind is without fear and the head is held high,</em>
                </strong>{" "}
                This initiative has touched lives across <strong>22 states in India</strong>, with the support
                of top funders, corporates, and partners. Over 18 years, it has impacted lakhs of lives through
                empowerment, education, and sustainable development in rural communities.
              </p>
              <p>
                Rajesh continues to be on the board as a Trustee – supporting the CEO of Head Held High even
                today.
              </p>
            </div>
          </div>

          <div className={Styles.roleSection}>
            <div className={Styles.logoContainer}>
              <img src={urls.oneBridgeLogo} alt="" className={Styles.roleLogo} />
            </div>
            <div>
              <h3 className={Styles.subSectionTitle}>Co-founder and CEO of 1Bridge</h3>
              <p>
                Rajesh Bhat focused on creating an ecosystem for rural entrepreneurs, serving as a bridge
                between urban and rural markets. His efforts empowered local businesses, facilitated innovation,
                and drove economic growth in rural India, building connections that addressed critical gaps in
                resources and opportunities.
              </p>
            </div>
          </div>

          <div className={Styles.roleSection}>
            <div className={Styles.logoContainer}>
              <img src={urls.ironLadyLogo} alt="" className={Styles.roleLogo} />
            </div>
            <div>
              <h3 className={Styles.subSectionTitle}>Iron Lady, Founder and Director</h3>
              <p>
                Rajesh founded Iron Lady with a singular goal: to help women step into their power as leaders.
                Under Rajesh&apos;s leadership, Iron Lady has grown into India&apos;s No. 1 platform for women
                interested in leadership, empowering over{" "}
                <strong>100+ women to achieve the 1CR+ income milestone.</strong>
              </p>
            </div>
          </div>

          <div className={Styles.roleSection}>
            <div className={Styles.logoContainer}>
              <img src={urls.winnerBenchLogo} alt="" className={Styles.roleLogo} />
            </div>
            <div>
              <h3 className={Styles.subSectionTitle}>Winner Bench, Co-founder</h3>
              <p>
                Winner Bench is the leading Educational Transformation initiative for Children. It focuses on
                creating a nurturing environment where children can thrive academically and personally. The
                initiative emphasizes developing not only academic skills but also essential life skills,
                empowering children to face challenges with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={`${bm.authority} ${Styles.bandDark}`} aria-labelledby="dreams-heading">
        <div className={Styles.pageInner}>
          <h2 id="dreams-heading" className={Styles.sectionTitle}>
            Making dreams a reality
          </h2>

          <div className={Styles.dreamBlock}>
            <h3 className={Styles.subSectionTitle}>From illiterate to 10th pass in 6 months</h3>
            <p>
              Rajesh co-founded <strong>Head Held High</strong> with a mission to uplift rural youth who were
              often overlooked and underserved. This groundbreaking initiative has empowered young individuals,
              transforming those who once struggled with basic literacy into confident, English-speaking
              professionals—all in just six months. Operating across <strong>22 states, Head Held High</strong>{" "}
              has impacted <strong>lakhs of lives</strong>, providing not just skills but dignity, opportunity,
              and hope for a brighter future.
            </p>
            <p>
              What truly sets this journey apart are the personal stories of transformation. A young girl who
              once couldn&apos;t imagine stepping outside her village is now leading a team in a bustling city.
              A boy, burdened by poverty, turned his family&apos;s fortunes around after gaining the skills and
              confidence to succeed. <strong>Head Held High</strong> is a testament to the power of education and
              belief, unlocking the potential of individuals and entire communities.
            </p>
          </div>

          <div className={Styles.dreamBlock}>
            <h3 className={Styles.subSectionTitle}>Empowering women to lead with Iron Lady</h3>
            <p>
              As a father of two daughters, Rajesh saw firsthand the questions and biases women face.{" "}
              <strong>&quot;Are women winning enough?&quot;</strong> became a question he couldn&apos;t ignore.
              This led him to establish <strong>Iron Lady</strong>, a platform designed to empower women with the
              skills, strategies, and confidence to take on leadership roles.
            </p>
            <p>
              Under Rajesh&apos;s guidance,{" "}
              <strong>
                Iron Lady has grown into a thriving community of over 76,000 women
              </strong>
              , each finding strength, support, and inspiration to rise together. But these aren&apos;t just
              numbers—they are stories of transformation. Stories of women reclaiming their voices, leading with
              authority in boardrooms, and rewriting their futures with purpose.
            </p>
            <p>
              More than <strong>100 women have achieved the remarkable milestone of earning 1CR+</strong>, a dream
              once thought impossible by many. Through life-changing programs like{" "}
              <strong>Leadership Essentials, 100 Board Members, and Master of Business Warfare</strong>, these
              women didn&apos;t just learn strategies—they discovered the courage to step up, break boundaries, and
              lead unapologetically.
            </p>
          </div>
        </div>
      </section>

      <section className={`${bm["video-section"]} ${Styles.band}`} aria-labelledby="legacy-heading">
        <div className={Styles.pageInner}>
          <h2 id="legacy-heading" className={Styles.sectionTitle}>
            A legacy of empowerment and recognition
          </h2>
          <div className={Styles.glassCard}>
            <p>
              Rajesh Bhat&apos;s journey is one of relentless determination to create meaningful change, earning
              him recognition and respect on both national and global platforms. His unwavering commitment to
              empowering underserved women has touched countless lives. By partnering with NGOs and educational
              institutions, Rajesh has run leadership workshops for underprivileged women, equipping them with the
              confidence and tools to shape their futures and uplift their communities. For this impactful work,{" "}
              <strong>CNN recognized him as one of &quot;The Real Heroes of India&quot;</strong>, celebrating his
              dedication to women&apos;s empowerment.
            </p>
            <p>
              But Rajesh&apos;s impact goes beyond individual success stories. His efforts have sparked a broader
              transformation, challenging long-held stereotypes and redefining how the world views women as
              leaders. Through bold and innovative leadership programs, he has created spaces where women can
              thrive, inspiring a larger conversation about gender equality in leadership.
            </p>
            <p>
              His visionary work has not gone unnoticed.{" "}
              <strong>
                Major media outlets have featured his pioneering leadership initiatives, amplifying his call for a
                more inclusive and equitable world
              </strong>
              . Rajesh&apos;s accomplishments stand as a testament to his mission of empowering others, fostering
              change, and building a legacy that inspires generations to come.
            </p>
          </div>
        </div>
      </section>

      <section className={`${bm.authority} ${Styles.bandDark}`} aria-labelledby="leader-heading">
        <div className={Styles.pageInner}>
          <h2 id="leader-heading" className={Styles.sectionTitle}>
            A leader who transforms lives
          </h2>
          <div className={Styles.glassCard}>
            <p>
              Rajesh Bhat&apos;s journey is a testament to the power of purpose and action. From empowering rural
              youth with Head Held High to breaking barriers for women through Iron Lady, he has consistently
              turned vision into impact.
            </p>
            <p>
              Rajesh isn&apos;t just creating programs—he&apos;s building movements that inspire change and challenge
              norms. His legacy reminds us that true leadership lies in transforming lives and creating a future
              where everyone can rise.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
